/**
 * GTCC (GENIUSTRACKER CONTROL COCKPIT) feature-flag registry.
 *
 * G1 ships the shell skeleton (Mono-Deck nav + topbar + content frame
 * + 17 stub views) behind a single flag, `GTCC_SHELL_ENABLED`,
 * default OFF. Same registry/env-parsing/resolve shape as
 * `sprint29-flags.ts`, but `resolveGtccFlags` is called directly from
 * the admin app with `import.meta.env` — G1 makes no new API call, so
 * there is no server-resolved flag table to fetch here.
 *
 * Env override convention: `GT_GTCC_<KEY without its GTCC_ prefix>=on|off`
 * (e.g. `GT_GTCC_SHELL_ENABLED`).
 */

export type GtccVisibilityClass = "public_testable" | "internal_admin_only" | "hidden_disabled";

export type GtccFlagKey = "GTCC_SHELL_ENABLED";

export interface GtccFlagDef {
  readonly key: GtccFlagKey;
  readonly wave: "G1";
  readonly visibility: GtccVisibilityClass;
  readonly defaultEnabled: boolean;
  readonly description: string;
  readonly qaNotes: string;
}

export const GTCC_FLAG_REGISTRY: Readonly<Record<GtccFlagKey, GtccFlagDef>> = {
  GTCC_SHELL_ENABLED: {
    key: "GTCC_SHELL_ENABLED",
    wave: "G1",
    visibility: "internal_admin_only",
    defaultEnabled: false,
    description:
      "GTCC Mono-Deck shell (6 nav groups / 17 stub views) in apps/admin. Platform-owner-only.",
    qaNotes:
      "Default off. Set `GT_GTCC_SHELL_ENABLED=on` (build-time Vite env, no fetch) to surface for a platform owner.",
  },
};

export const GTCC_FLAG_KEYS: readonly GtccFlagKey[] = Object.keys(
  GTCC_FLAG_REGISTRY,
) as GtccFlagKey[];

export function envVarNameForGtccFlag(key: GtccFlagKey): string {
  const suffix = key.startsWith("GTCC_") ? key.slice("GTCC_".length) : key;
  return `GT_GTCC_${suffix}`;
}

export function parseGtccFlagEnv(value: string | undefined): boolean | undefined {
  if (value === undefined) return undefined;
  const trimmed = value.trim().toLowerCase();
  if (trimmed === "") return undefined;
  if (["on", "true", "1", "yes", "y", "enabled"].includes(trimmed)) return true;
  if (["off", "false", "0", "no", "n", "disabled"].includes(trimmed)) return false;
  return undefined;
}

export interface ResolvedGtccFlag {
  readonly key: GtccFlagKey;
  readonly wave: GtccFlagDef["wave"];
  readonly visibility: GtccVisibilityClass;
  readonly enabled: boolean;
  readonly description: string;
  readonly source: "default" | "env";
}

export type ResolvedGtccFlags = Readonly<Record<GtccFlagKey, ResolvedGtccFlag>>;

export interface ResolveGtccFlagsOptions {
  readonly env?: NodeJS.ProcessEnv | Record<string, string | undefined>;
}

export function resolveGtccFlags(opts: ResolveGtccFlagsOptions = {}): ResolvedGtccFlags {
  const env = opts.env ?? process.env;
  const resolved: Partial<Record<GtccFlagKey, ResolvedGtccFlag>> = {};
  for (const key of GTCC_FLAG_KEYS) {
    const def = GTCC_FLAG_REGISTRY[key];
    const envName = envVarNameForGtccFlag(key);
    const fromEnv = parseGtccFlagEnv(env[envName]);
    const enabled = fromEnv ?? def.defaultEnabled;
    resolved[key] = {
      key,
      wave: def.wave,
      visibility: def.visibility,
      enabled,
      description: def.description,
      source: fromEnv === undefined ? "default" : "env",
    };
  }
  return resolved as ResolvedGtccFlags;
}

const ADMIN_ROLES: readonly string[] = [
  "tenant.admin",
  "tenant.owner",
  "platform.owner",
  "platform.admin",
  "auditor",
];

export function isGtccFlagVisible(flag: ResolvedGtccFlag, viewerRoles: readonly string[]): boolean {
  if (!flag.enabled) return false;
  switch (flag.visibility) {
    case "public_testable":
      return true;
    case "internal_admin_only":
      return viewerRoles.some((r) => ADMIN_ROLES.includes(r));
    case "hidden_disabled":
      return false;
    default:
      return false;
  }
}
